import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  LayoutDashboard,
  BookOpen,
  FileText,
  Users,
  Landmark,
  ArrowLeftRight,
  FileCheck2,
  ChartNoAxesColumn,
  Settings2,
  ChevronDown,
  Plus,
  Search,
  ArrowUpRight,
  Check,
  X,
  Download,
  Upload,
  CircleHelp,
  LogOut,
  ShieldCheck,
  Building2,
  CalendarDays,
  History,
  ChevronRight,
  CheckCircle2,
  Circle,
  Loader2,
  RotateCcw,
  LockKeyhole,
} from "lucide-react";
import { api, all, post, backup, fmt, cent, decimal, type Row } from "./api";
import {
  AnaliticaPage,
  DistributionEditor,
  DistributionForm,
} from "./Analitica";

import { AccountSelect } from "./AccountSelect";

type Page =
  | "resumen"
  | "cuentas"
  | "asientos"
  | "comprobantes"
  | "terceros"
  | "cartera"
  | "bancos"
  | "ats"
  | "reportes"
  | "configuracion"
  | "analitica"
  | "auditoria";
type Field = {
  name: string;
  label: string;
  type?: string;
  options?: { value: any; label: string }[];
  searchable?: boolean;
  required?: boolean;
  placeholder?: string;
  span?: boolean;
  default?: any;
};
type Dialog = {
  title: string;
  subtitle?: string;
  fields?: Field[];
  initial?: Row;
  submit?: (values: Row) => Promise<void>;
  kind?: string;
  content?: ReactNode;
};
const NAV: {
  id: Page;
  label: string;
  icon: typeof BookOpen;
  group?: string;
}[] = [
  { id: "resumen", label: "Resumen", icon: LayoutDashboard },
  {
    id: "cuentas",
    label: "Plan de cuentas",
    icon: BookOpen,
    group: "CONTABILIDAD",
  },
  { id: "asientos", label: "Asientos contables", icon: FileText },
  { id: "analitica", label: "Proyectos y centros de costos", icon: Building2 },
  { id: "comprobantes", label: "Comprobantes", icon: FileCheck2 },
  { id: "terceros", label: "Clientes y proveedores", icon: Users },
  { id: "cartera", label: "Cuentas por cobrar y pagar", icon: ArrowLeftRight },
  {
    id: "bancos",
    label: "Caja y bancos",
    icon: Landmark,
    group: "CONTROL Y OBLIGACIONES",
  },
  { id: "ats", label: "Anexo transaccional · ATS", icon: FileCheck2 },
  { id: "reportes", label: "Reportes", icon: ChartNoAxesColumn },
  { id: "auditoria", label: "Historial de actividad", icon: History },
  {
    id: "configuracion",
    label: "Configuración",
    icon: Settings2,
    group: "ADMINISTRACIÓN",
  },
];
const TITLES: Record<Page, [string, string]> = {
  analitica: [
    "Proyectos y centros de costos",
    "Clasifica tu operación por área y por trabajo.",
  ],
  resumen: [
    "Tu contabilidad, en orden",
    "Una vista clara de la operación de tu empresa.",
  ],
  cuentas: [
    "Plan de cuentas",
    "Organiza las cuentas y su estructura contable.",
  ],
  asientos: [
    "Asientos contables",
    "Cada movimiento tiene un origen y una historia.",
  ],
  comprobantes: [
    "Comprobantes",
    "Revisa, clasifica y contabiliza compras y ventas.",
  ],
  terceros: [
    "Clientes y proveedores",
    "La información de las personas con las que trabajas.",
  ],
  cartera: [
    "Cuentas por cobrar y pagar",
    "Saldos pendientes, vencimientos y aplicaciones.",
  ],
  bancos: ["Caja y bancos", "Movimientos, extractos y conciliación bancaria."],
  ats: [
    "Anexo transaccional simplificado",
    "Prepara y verifica la información de tu período fiscal.",
  ],
  reportes: [
    "Reportes contables",
    "Del estado financiero al comprobante de origen.",
  ],
  configuracion: [
    "Configuración",
    "Empresas, reglas contables y control de períodos.",
  ],
  auditoria: [
    "Historial de actividad",
    "Trazabilidad de las operaciones de esta empresa.",
  ],
};
const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
function Button({
  children,
  onClick,
  secondary = false,
  disabled = false,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  secondary?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      className={`btn ${secondary ? "secondary" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`badge ${tone}`}>{children}</span>;
}
function Empty({
  text = "Todavía no hay registros en esta vista.",
}: {
  text?: string;
}) {
  return (
    <div className="empty">
      <BookOpen size={28} />
      <p>{text}</p>
      <span>Los registros aparecerán aquí cuando comiences a trabajar.</span>
    </div>
  );
}
function Table({
  columns,
  rows,
  render,
}: {
  columns: string[];
  rows: Row[];
  render: (r: Row) => ReactNode;
}) {
  return rows.length ? (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i}>{render(r)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty />
  );
}
function FieldInput({
  f,
  value,
  onChange,
}: {
  f: Field;
  value: any;
  onChange: (v: any) => void;
}) {
  if (f.searchable && f.options)
    return (
      <AccountSelect
        label={f.label}
        options={f.options}
        value={value}
        onChange={onChange}
        required={f.required !== false}
        span={f.span}
      />
    );
  return (
    <label className={f.span ? "span2" : ""}>
      <span>
        {f.label}
        {f.required !== false && <small> *</small>}
      </span>
      {f.options ? (
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          required={f.required !== false}
        >
          <option value="">Seleccionar…</option>
          {f.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : f.type === "textarea" ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          required={f.required !== false}
        />
      ) : (
        <input
          type={f.type || "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.placeholder}
          required={f.required !== false}
          step={f.type === "number" ? "0.01" : undefined}
        />
      )}
    </label>
  );
}

export default function App() {
  const [session, setSession] = useState<Row | null>(null);
  const [companies, setCompanies] = useState<Row[]>([]);
  const [company, setCompany] = useState("");
  const [period, setPeriod] = useState("");
  const [page, setPage] = useState<Page>("resumen");
  const [data, setData] = useState<Record<string, Row[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [search, setSearch] = useState("");
  const [revision, setRevision] = useState(0);
  const [reportKind, setReportKind] = useState("balance");
  const [rows, setRows] = useState<Row[]>([]);
  const [summary, setSummary] = useState<Row>({});
  const [accountPlan, setAccountPlan] = useState<{
    scope: string;
    rows: Row[];
    error?: string;
  } | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Row | null>(null);
  const [accountLedger, setAccountLedger] = useState<Row[] | null>(null);
  const [accountLedgerError, setAccountLedgerError] = useState("");
  const [reportAccount, setReportAccount] = useState("");
  const [reportThird, setReportThird] = useState("");
  const [reportCenter, setReportCenter] = useState("");
  const [reportProject, setReportProject] = useState("");
  const [analyticsTab, setAnalyticsTab] = useState("centros-costo");
  const [detail, setDetail] = useState<Row | null>(null);
  const base = `empresas/${company}/`;
  const active = companies.find((c) => String(c.id) === company);
  const current = data.periodos?.find((p) => String(p.id) === period);
  const year = current
    ? data.ejercicios?.find((e) => e.id === current.ejercicio)?.anio || 2026
    : 2026;
  const month = current?.mes || 10;
  const start = `${year}-${String(month).padStart(2, "0")}-01`;
  const end = `${year}-${String(month).padStart(2, "0")}-${new Date(year, month, 0).getDate()}`;
  const planScope = `${company}:${start}:${end}:${revision}`;
  const plan = accountPlan?.scope === planScope ? accountPlan : null;
  const accountValues = new Map((plan?.rows || []).map((r) => [r.id, r]));
  const refresh = () => setRevision((r) => r + 1);
  useEffect(() => {
    api("sesion/")
      .then(setSession)
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    if (session?.usuario)
      all("empresas/")
        .then((c) => {
          setCompanies(c);
          setCompany((v) =>
            c.some((x) => String(x.id) === v) ? v : String(c[0]?.id || ""),
          );
        })
        .catch((e) => setError(e.message));
  }, [session?.usuario, revision]);
  useEffect(() => {
    if (!company || !session?.usuario) return;
    let cancelled = false;
    setBusy(true);
    setData({});
    const keys = [
      "cuentas",
      "terceros",
      "periodos",
      "ejercicios",
      "asientos",
      "comprobantes",
      "tesoreria",
      "movimientos",
      "extractos",
      "extracto-lineas",
      "ats",
      "auditoria",
      "reglas",
      "miembros",
      "perfiles",
      "establecimientos",
      "centros-costo",
      "proyectos",
      "retenciones",
    ];
    Promise.all(keys.map(async (k) => [k, await all(base + k + "/")]))
      .then((entries) => {
        if (cancelled) return;
        const next = Object.fromEntries(entries);
        setData(next);
        setPeriod((old) =>
          next.periodos.some((p: Row) => String(p.id) === old)
            ? old
            : String(
                next.periodos.find((p: Row) => p.mes === 10)?.id ||
                  next.periodos[0]?.id ||
                  "",
              ),
        );
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [company, session?.usuario, revision]);
  useEffect(() => {
    if (!company || !period) return;
    let cancelled = false;
    api(
      `${base}reportes/${reportKind}/?desde=${start}&hasta=${end}&cuenta=${reportAccount}&tercero=${reportThird}&centro_costo=${reportCenter}&proyecto=${reportProject}`,
    )
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [
    company,
    period,
    reportKind,
    revision,
    start,
    end,
    reportAccount,
    reportThird,
    reportCenter,
    reportProject,
  ]);
  useEffect(() => {
    if (!company || !period) return;
    let cancelled = false;
    api(`${base}reportes/resumen/?desde=${start}&hasta=${end}`)
      .then((r) => {
        if (!cancelled) setSummary(r[0] || {});
      })
      .catch((e) => setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [company, period, revision, start, end]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    setSelectedAccount(null);
    if (!company || !period || !current || busy || page !== "cuentas") return;
    let cancelled = false;
    setAccountPlan(null);
    api(`${base}reportes/plan-cuentas/?desde=${start}&hasta=${end}`)
      .then((rows) => {
        if (!cancelled) setAccountPlan({ scope: planScope, rows });
      })
      .catch((e) => {
        if (!cancelled)
          setAccountPlan({ scope: planScope, rows: [], error: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [company, period, current?.id, busy, page, revision, start, end]);
  useEffect(() => {
    if (!selectedAccount || page !== "cuentas") return;
    let cancelled = false;
    setAccountLedger(null);
    setAccountLedgerError("");
    api(
      `${base}reportes/mayor/?desde=${start}&hasta=${end}&cuenta=${selectedAccount.id}`,
    )
      .then((rows) => {
        if (!cancelled) setAccountLedger(rows);
      })
      .catch((e) => {
        if (!cancelled) setAccountLedgerError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccount, company, page, start, end]);
  const run = async (
    fn: () => Promise<any>,
    message = "Operación completada.",
  ) => {
    setError("");
    try {
      await fn();
      setDialog(null);
      setToast(message);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const options = (
    key: string,
    label: (r: Row) => string,
    filter?: (r: Row) => boolean,
  ) =>
    (data[key] || [])
      .filter(filter || (() => true))
      .map((r) => ({ value: r.id, label: label(r) }));
  const accountOptions = options(
    "cuentas",
    (r) => `${r.codigo} · ${r.nombre}`,
    (r) => r.movimiento && r.activa,
  );
  const thirdOptions = options("terceros", (r) => r.nombre);
  const bankOptions = options("tesoreria", (r) => r.nombre);
  const form = (
    title: string,
    fields: Field[],
    endpoint: string,
    transform?: (v: Row) => any,
    initial?: Row,
  ) =>
    setDialog({
      title,
      fields,
      initial,
      submit: async (v) => {
        await post(base + endpoint + "/", transform ? transform(v) : v);
      },
    });
  const reason = (title: string, endpoint: string, fecha = false) =>
    form(
      title,
      [
        { name: "motivo", label: "Motivo", span: true },
        {
          name: "fecha",
          label: "Fecha",
          type: "date",
          required: fecha,
          default: start,
        },
      ],
      endpoint,
    );
  const navigate = (p: Page) => {
    setPage(p);
    setSearch("");
    setDetail(null);
  };
  const filtered = (key: string) =>
    (data[key] || []).filter(
      (r) =>
        !search ||
        JSON.stringify(r).toLowerCase().includes(search.toLowerCase()),
    );
  const inPeriod = (r: Row) =>
    String(r.fecha || r.fecha_registro || "").slice(0, 7) === start.slice(0, 7);
  const docs = (data.comprobantes || []).filter(inPeriod);
  const entries = (data.asientos || []).filter(inPeriod);
  const showDoc = async (id: any) => {
    try {
      setDetail(await api(`${base}comprobantes/${id}/detalle/`));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const showEntry = async (id: any) => {
    try {
      setDetail({
        asiento: (data.asientos || []).find((x) => x.id === id) || { id },
        lineas: await api(`${base}asientos/${id}/lineas/`),
      });
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const newThird = () =>
    form(
      "Nuevo cliente o proveedor",
      [
        { name: "nombre", label: "Nombre o razón social", span: true },
        {
          name: "tipo_identificacion",
          label: "Tipo de identificación",
          options: [
            { value: "04", label: "RUC" },
            { value: "05", label: "Cédula" },
            { value: "07", label: "Consumidor final" },
          ],
          default: "04",
        },
        { name: "identificacion", label: "Identificación" },
        {
          name: "email",
          label: "Correo electrónico",
          type: "email",
          required: false,
        },
        { name: "direccion", label: "Dirección", required: false },
        {
          name: "rol",
          label: "Relación comercial",
          options: [
            { value: "cliente", label: "Cliente" },
            { value: "proveedor", label: "Proveedor" },
            { value: "ambos", label: "Cliente y proveedor" },
          ],
          default: "ambos",
        },
      ],
      "terceros",
      ({ rol, ...tercero }) => ({
        ...tercero,
        cliente: rol !== "proveedor",
        proveedor: rol !== "cliente",
      }),
    );
  const newAccount = () =>
    form(
      "Nueva cuenta contable",
      [
        { name: "codigo", label: "Código" },
        { name: "nombre", label: "Nombre" },
        {
          name: "grupo",
          label: "Grupo",
          options: [
            "activo",
            "pasivo",
            "patrimonio",
            "ingreso",
            "costo",
            "gasto",
          ].map((g) => ({ value: g, label: g })),
          default: "gasto",
        },
        {
          name: "naturaleza",
          label: "Naturaleza",
          options: [
            { value: "deudora", label: "Deudora" },
            { value: "acreedora", label: "Acreedora" },
          ],
          default: "deudora",
        },
        {
          name: "padre",
          label: "Cuenta padre",
          searchable: true,
          options: options(
            "cuentas",
            (r) => `${r.codigo} · ${r.nombre}`,
            (r) => !r.movimiento,
          ),
          required: false,
        },
        {
          name: "movimiento",
          label: "Clase de cuenta",
          options: [
            { value: "si", label: "De movimiento" },
            { value: "no", label: "De agrupación" },
          ],
          default: "si",
        },
      ],
      "cuentas",
      (v) => ({
        ...v,
        padre: v.padre || null,
        movimiento: v.movimiento === "si",
      }),
    );
  const newCompany = () =>
    setDialog({
      title: "Nueva empresa",
      fields: [
        { name: "razon_social", label: "Razón social", span: true },
        { name: "ruc", label: "RUC" },
        { name: "direccion", label: "Dirección", required: false },
        { name: "anio", label: "Ejercicio inicial", default: "2026" },
      ],
      submit: async (v) => {
        const e = await post("empresas/", {
          razon_social: v.razon_social,
          ruc: v.ruc,
          direccion: v.direccion,
        });
        await post(`empresas/${e.id}/inicializar/`, { anio: Number(v.anio) });
        setCompany(String(e.id));
      },
    });
  if (!session)
    return (
      <div className="loading-screen">
        <Loader2 className="spin" /> Cargando Contable…
      </div>
    );
  if (!session.usuario)
    return <Login onLogin={setSession} error={error} setError={setError} />;
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-symbol">
            <BookOpen size={23} />
          </div>
          <div>
            contable<span>ECUADOR</span>
          </div>
        </div>
        <div className="workspace-label">ESPACIO DE TRABAJO</div>
        <button
          className="company-card"
          onClick={() => navigate("configuracion")}
        >
          <div className="company-avatar">
            {active?.razon_social?.slice(0, 1) || "E"}
          </div>
          <span>
            <strong>{active?.razon_social || "Selecciona una empresa"}</strong>
            <small>{active?.ruc || "Configuración inicial"}</small>
          </span>
          <ChevronDown size={14} />
        </button>
        <nav>
          {NAV.map((n) => (
            <div key={n.id}>
              {n.group && <div className="nav-group">{n.group}</div>}
              <button
                className={page === n.id ? "nav-item active" : "nav-item"}
                onClick={() => navigate(n.id)}
              >
                <n.icon size={18} />
                <span>{n.label}</span>
                {n.id === "comprobantes" &&
                  docs.filter((d) => d.estado === "borrador").length > 0 && (
                    <i>{docs.filter((d) => d.estado === "borrador").length}</i>
                  )}
              </button>
            </div>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="local-state">
            <span className="status-dot" />
            <div>
              Entorno conectado<small>Datos en PostgreSQL</small>
            </div>
            <ShieldCheck size={16} />
          </div>
          <button
            className="user-button"
            onClick={() =>
              api("sesion/", { method: "DELETE" }).then(() => {
                setSession({ usuario: null });
                setCompanies([]);
                setData({});
              })
            }
          >
            <div className="user-avatar">
              {session.usuario.slice(0, 1).toUpperCase()}
            </div>
            <span>
              <strong>{session.usuario}</strong>
              <small>Cerrar sesión</small>
            </span>
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div className="breadcrumb">
            Espacio de trabajo <ChevronRight size={13} />
            <span>{NAV.find((n) => n.id === page)?.label}</span>
          </div>
          <div className="topbar-actions">
            <span className="local-pill">
              <span className="status-dot" />
              Local
            </span>
            <button
              className="icon-button"
              title="Guía de uso"
              onClick={() =>
                setDialog({
                  title: "Cómo comenzar",
                  content: (
                    <div className="help">
                      <p>
                        1. Configura la empresa, su perfil tributario y el plan
                        de cuentas.
                      </p>
                      <p>
                        2. Crea terceros e importa XML o registra comprobantes
                        manualmente.
                      </p>
                      <p>3. Revisa las cuentas e impuestos y contabiliza.</p>
                      <p>
                        4. Registra cobros y pagos; importa y concilia
                        extractos.
                      </p>
                      <p>5. Revisa reportes, genera ATS y cierra el período.</p>
                      <p>
                        Los datos de demostración son ficticios. El ATS requiere
                        contraste con el validador oficial antes de su
                        presentación.
                      </p>
                      <a href="/api/v1/docs/" target="_blank" rel="noreferrer">
                        Documentación de la API ↗
                      </a>
                    </div>
                  ),
                })
              }
            >
              <CircleHelp size={19} />
            </button>
            <div className="tiny-avatar">
              {session.usuario.slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <div className="content">
          <div className="page-heading">
            <div>
              <div className="eyebrow">
                {active?.razon_social || "BIENVENIDO A CONTABLE"}
              </div>
              <h1>{TITLES[page][0]}</h1>
              <p>{TITLES[page][1]}</p>
            </div>
            <div className="heading-actions">
              <label className="select-shell">
                <Building2 size={15} />
                <select
                  aria-label="Empresa"
                  value={company}
                  onChange={(e) => {
                    setReportCenter("");
                    setReportProject("");
                    setReportAccount("");
                    setReportThird("");
                    setRows([]);
                    setSummary({});
                    setCompany(e.target.value);
                  }}
                >
                  {companies.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.razon_social}
                    </option>
                  ))}
                </select>
              </label>
              <label className="select-shell">
                <CalendarDays size={15} />
                <select
                  aria-label="Período"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {(data.periodos || []).map((p) => (
                    <option value={p.id} key={p.id}>
                      {MONTHS[p.mes - 1]}{" "}
                      {data.ejercicios?.find((e) => e.id === p.ejercicio)
                        ?.anio || 2026}
                      {p.cerrado ? " · Cerrado" : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          {error && (
            <div className="alert error" role="alert">
              <span>{error}</span>
              <button onClick={() => setError("")}>
                <X size={16} />
              </button>
            </div>
          )}
          {active?.demo && (
            <div className="demo-note">
              <span className="demo-mark">PRUEBAS</span>Estás trabajando con una
              empresa de demostración y datos ficticios.
              <span>USD · Régimen general</span>
            </div>
          )}
          {!company && (
            <section className="panel">
              <Empty text="Crea tu primera empresa para comenzar." />
              <Button onClick={newCompany}>
                <Plus size={16} />
                Crear empresa
              </Button>
            </section>
          )}
          {busy ? (
            <div className="busy">
              <Loader2 className="spin" />
              Actualizando registros…
            </div>
          ) : (
            company && (
              <>
                {page === "resumen" && (
                  <>
                    <div className="welcome-line">
                      <span>
                        <span className="status-dot" /> {MONTHS[month - 1]}{" "}
                        {year} <span className="muted">/</span>{" "}
                        {current?.cerrado
                          ? "Período cerrado"
                          : "Período abierto"}
                      </span>
                      <Button
                        onClick={() =>
                          setDialog({
                            title: "Nuevo comprobante",
                            kind: "documento",
                          })
                        }
                      >
                        <Plus size={17} />
                        Nuevo comprobante
                      </Button>
                    </div>
                    <div className="metrics">
                      <Metric
                        label="Ingresos por servicios"
                        value={fmt(summary.ingresos)}
                        icon={<ArrowUpRight size={19} />}
                        note="Ingresos contabilizados en el período"
                      />
                      <Metric
                        label="Compras y gastos"
                        value={fmt(summary.gastos)}
                        icon={<ArrowLeftRight size={19} />}
                        note="Costos y gastos contabilizados"
                      />
                      <Metric
                        label="Resultado operativo"
                        value={fmt(summary.resultado)}
                        icon={<ChartNoAxesColumn size={19} />}
                        note="Resultado según los movimientos del mayor"
                        accent
                      />
                      <Metric
                        label="Comprobantes registrados"
                        value={String(docs.length).padStart(2, "0")}
                        icon={<FileText size={19} />}
                        note={`${docs.filter((d) => d.estado === "borrador").length} pendientes de contabilizar`}
                      />
                    </div>
                    <div className="dashboard-grid">
                      <section className="panel recent-panel">
                        <div className="panel-heading">
                          <div>
                            <h2>Actividad contable reciente</h2>
                            <p>Últimos movimientos del período</p>
                          </div>
                          <button
                            className="text-button"
                            onClick={() => navigate("asientos")}
                          >
                            Ver todos <ArrowUpRight size={15} />
                          </button>
                        </div>
                        <Table
                          columns={[
                            "Fecha / referencia",
                            "Concepto",
                            "Estado",
                            "Asiento",
                          ]}
                          rows={entries.slice(0, 7)}
                          render={(a) => (
                            <>
                              <td>
                                <strong>{a.fecha}</strong>
                                <small>
                                  {a.tipo} · {a.numero || "Borrador"}
                                </small>
                              </td>
                              <td className="concept-cell">{a.concepto}</td>
                              <td>
                                <Badge
                                  tone={
                                    a.estado === "contabilizado"
                                      ? "green"
                                      : "amber"
                                  }
                                >
                                  {a.estado === "contabilizado"
                                    ? "Contabilizado"
                                    : "Borrador"}
                                </Badge>
                              </td>
                              <td>
                                <button
                                  className="row-link"
                                  onClick={() => showEntry(a.id)}
                                >
                                  #{a.id}
                                  <ArrowUpRight size={13} />
                                </button>
                              </td>
                            </>
                          )}
                        />
                        <div className="panel-footer">
                          <ShieldCheck size={15} /> Los asientos contabilizados
                          conservan su historial.
                        </div>
                      </section>
                      <section className="panel close-panel">
                        <div className="panel-heading">
                          <div>
                            <h2>Tu cierre de mes</h2>
                            <p>Todo lo que necesitas revisar</p>
                          </div>
                          <CalendarDays size={19} />
                        </div>
                        <div className="progress-summary">
                          <strong>
                            {
                              [
                                docs.length > 0,
                                docs.length > 0 &&
                                  !docs.some((d) => d.estado === "borrador"),
                                (data.extractos || []).length > 0,
                                (data.ats || []).some(
                                  (a) =>
                                    a.periodo === Number(period) &&
                                    !a.errores.length,
                                ),
                              ].filter(Boolean).length
                            }{" "}
                            de 4
                          </strong>
                          <span>pasos preparados</span>
                        </div>
                        <Checklist
                          done={docs.length > 0}
                          title="Registrar comprobantes"
                          sub={`${docs.length} documentos en el período`}
                          onClick={() => navigate("comprobantes")}
                        />
                        <Checklist
                          done={
                            docs.length > 0 &&
                            !docs.some((d) => d.estado === "borrador")
                          }
                          title="Revisar la contabilidad"
                          sub="Clasificación e impuestos completos"
                          onClick={() => navigate("asientos")}
                        />
                        <Checklist
                          done={(data.extractos || []).length > 0}
                          title="Revisar bancos"
                          sub="Verifica la diferencia sin explicar"
                          onClick={() => navigate("bancos")}
                        />
                        <Checklist
                          done={(data.ats || []).some(
                            (a) =>
                              a.periodo === Number(period) && !a.errores.length,
                          )}
                          title="Preparar el ATS"
                          sub="Validación XML y revisión tributaria"
                          onClick={() => navigate("ats")}
                        />
                        <button
                          className="close-link"
                          onClick={() => navigate("configuracion")}
                        >
                          Ir al control de períodos <ChevronRight size={15} />
                        </button>
                      </section>
                    </div>
                    <div className="quick-grid">
                      <button
                        onClick={() =>
                          setDialog({
                            title: "Importar comprobantes XML",
                            kind: "xml",
                          })
                        }
                      >
                        <span className="quick-icon">
                          <Upload size={20} />
                        </span>
                        <div>
                          <strong>Importar comprobantes</strong>
                          <small>Desde archivos XML del SRI</small>
                        </div>
                        <ArrowUpRight size={17} />
                      </button>
                      <button
                        onClick={() => {
                          setReportKind("balance");
                          navigate("reportes");
                        }}
                      >
                        <span className="quick-icon">
                          <BookOpen size={20} />
                        </span>
                        <div>
                          <strong>Balance de comprobación</strong>
                          <small>Consulta tus saldos y movimientos</small>
                        </div>
                        <ArrowUpRight size={17} />
                      </button>
                      <button
                        onClick={() =>
                          run(backup, "Respaldo creado y descargado.")
                        }
                      >
                        <span className="quick-icon">
                          <ShieldCheck size={20} />
                        </span>
                        <div>
                          <strong>Respaldar información</strong>
                          <small>Base de datos y documentos</small>
                        </div>
                        <ArrowUpRight size={17} />
                      </button>
                    </div>
                  </>
                )}
                {page === "cuentas" && (
                  <section className="panel">
                    <Toolbar
                      title={`${(data.cuentas || []).length} cuentas contables`}
                      search={search}
                      setSearch={setSearch}
                    >
                      <Button
                        secondary
                        onClick={() =>
                          setDialog({
                            title: "Importar plan de cuentas",
                            kind: "maestros-cuentas",
                          })
                        }
                      >
                        <Upload size={16} />
                        Importar
                      </Button>
                      <Button onClick={newAccount}>
                        <Plus size={16} />
                        Nueva cuenta
                      </Button>
                    </Toolbar>
                    <p className="account-period">
                      Del {start} al {end} · Solo asientos contabilizados.
                      Saldos según la naturaleza de cada cuenta; las
                      agrupaciones incluyen sus subcuentas.
                    </p>
                    {!plan && (
                      <p className="account-period" role="status">
                        Cargando valores de las cuentas…
                      </p>
                    )}
                    {plan?.error && (
                      <div className="alert error" role="alert">
                        No se pudieron cargar los valores: {plan.error}
                        <button className="row-link" onClick={refresh}>
                          Reintentar
                        </button>
                      </div>
                    )}
                    <Table
                      columns={[
                        "Código",
                        "Cuenta",
                        "Saldo inicial",
                        "Debe",
                        "Haber",
                        "Saldo final",
                        "Estado",
                        "Acciones",
                      ]}
                      rows={filtered("cuentas")}
                      render={(c) => (
                        <>
                          <td>
                            <code>{c.codigo}</code>
                          </td>
                          <td className={!c.movimiento ? "account-parent" : ""}>
                            {c.nombre}
                            <small>
                              {c.grupo} · {c.naturaleza} ·{" "}
                              {c.movimiento ? "Movimiento" : "Agrupación"}
                            </small>
                          </td>
                          {["inicial", "debe", "haber", "saldo"].map(
                            (field) => (
                              <td
                                key={field}
                                className={`amount ${!c.movimiento ? "account-parent" : ""}`}
                              >
                                {accountValues.has(c.id)
                                  ? fmt(accountValues.get(c.id)![field])
                                  : plan?.error
                                    ? "—"
                                    : "…"}
                              </td>
                            ),
                          )}
                          <td>
                            <span
                              className={`inline-state ${c.activa ? "green" : ""}`}
                            >
                              {c.activa ? "Activa" : "Inactiva"}
                            </span>
                          </td>
                          <td className="row-actions">
                            <button
                              className="row-link"
                              disabled={!accountValues.has(c.id)}
                              onClick={() =>
                                setSelectedAccount({
                                  ...c,
                                  ...accountValues.get(c.id),
                                  scope: planScope,
                                })
                              }
                            >
                              Ver detalle
                            </button>{" "}
                            <button
                              className="row-link"
                              onClick={() =>
                                setDialog({
                                  title: "Editar cuenta",
                                  fields: [
                                    {
                                      name: "nombre",
                                      label: "Nombre",
                                      span: true,
                                    },
                                    {
                                      name: "activa",
                                      label: "Estado",
                                      options: [
                                        { value: "si", label: "Activa" },
                                        { value: "no", label: "Inactiva" },
                                      ],
                                    },
                                  ],
                                  initial: {
                                    nombre: c.nombre,
                                    activa: c.activa ? "si" : "no",
                                  },
                                  submit: async (v) => {
                                    await api(`${base}cuentas/${c.id}/`, {
                                      method: "PATCH",
                                      body: JSON.stringify({
                                        nombre: v.nombre,
                                        activa: v.activa === "si",
                                      }),
                                    });
                                  },
                                })
                              }
                            >
                              Editar
                            </button>
                          </td>
                        </>
                      )}
                    />
                  </section>
                )}
                {page === "asientos" && (
                  <section className="panel">
                    <Toolbar
                      title="Libro diario"
                      search={search}
                      setSearch={setSearch}
                    >
                      <Button
                        onClick={() =>
                          setDialog({
                            title: "Nuevo asiento contable",
                            kind: "asiento",
                          })
                        }
                      >
                        <Plus size={16} />
                        Nuevo asiento
                      </Button>
                    </Toolbar>
                    <Table
                      columns={[
                        "Fecha",
                        "Número",
                        "Concepto",
                        "Estado",
                        "Acciones",
                      ]}
                      rows={filtered("asientos").filter(inPeriod)}
                      render={(a) => (
                        <>
                          <td>{a.fecha}</td>
                          <td>
                            <code>
                              {a.tipo}-{a.numero || "—"}
                            </code>
                          </td>
                          <td>{a.concepto}</td>
                          <td>
                            <Badge
                              tone={
                                a.estado === "contabilizado" ? "green" : "amber"
                              }
                            >
                              {a.estado}
                            </Badge>
                          </td>
                          <td className="row-actions">
                            <button onClick={() => showEntry(a.id)}>Ver</button>
                            {a.estado === "borrador" ? (
                              <button
                                onClick={() =>
                                  run(() =>
                                    post(
                                      `${base}asientos/${a.id}/contabilizar/`,
                                      {},
                                    ),
                                  )
                                }
                              >
                                Contabilizar
                              </button>
                            ) : (
                              !a.reversa_de && (
                                <button
                                  onClick={() =>
                                    reason(
                                      "Reversar asiento",
                                      `asientos/${a.id}/reversar`,
                                      true,
                                    )
                                  }
                                >
                                  Reversar
                                </button>
                              )
                            )}
                          </td>
                        </>
                      )}
                    />
                  </section>
                )}
                {page === "comprobantes" && (
                  <section className="panel">
                    <Toolbar
                      title="Compras y ventas"
                      search={search}
                      setSearch={setSearch}
                    >
                      <Button
                        secondary
                        onClick={() =>
                          setDialog({
                            title: "Importar comprobantes XML",
                            kind: "xml",
                          })
                        }
                      >
                        <Upload size={16} />
                        Importar XML
                      </Button>
                      <Button
                        onClick={() =>
                          setDialog({
                            title: "Nuevo comprobante",
                            kind: "documento",
                          })
                        }
                      >
                        <Plus size={16} />
                        Nuevo comprobante
                      </Button>
                    </Toolbar>
                    <Table
                      columns={[
                        "Documento",
                        "Tercero / concepto",
                        "Operación",
                        "Total",
                        "Estado",
                        "Acciones",
                      ]}
                      rows={filtered("comprobantes").filter(inPeriod)}
                      render={(d) => (
                        <>
                          <td>
                            <strong>
                              {d.establecimiento}-{d.punto_emision}-
                              {d.secuencial}
                            </strong>
                            <small>
                              {d.fecha_emision} ·{" "}
                              {d.electronico ? "Electrónico" : "Físico"} · tipo{" "}
                              {d.tipo}
                            </small>
                          </td>
                          <td>
                            <strong>{d.tercero_nombre}</strong>
                            <small>{d.concepto}</small>
                          </td>
                          <td>
                            <Badge
                              tone={
                                d.operacion === "venta" ? "blue" : "neutral"
                              }
                            >
                              {d.operacion}
                            </Badge>
                          </td>
                          <td className="amount">{fmt(d.total)}</td>
                          <td>
                            <Badge
                              tone={
                                d.estado === "contabilizado"
                                  ? "green"
                                  : d.estado === "anulado"
                                    ? "neutral"
                                    : "amber"
                              }
                            >
                              {d.estado}
                            </Badge>
                          </td>
                          <td className="row-actions">
                            <button onClick={() => showDoc(d.id)}>Ver</button>
                            {d.estado === "borrador" && (
                              <>
                                <button
                                  onClick={() =>
                                    setDialog({
                                      title: "Revisar comprobante",
                                      fields: [
                                        {
                                          name: "cuenta",
                                          label: "Cuenta de clasificación",
                                          options: accountOptions,
                                          searchable: true,
                                          span: true,
                                        },
                                        {
                                          name: "autorizacion",
                                          label: "Autorización",
                                        },
                                        {
                                          name: "sustento",
                                          label: "Sustento tributario",
                                          default: "01",
                                        },
                                        {
                                          name: "forma_pago",
                                          label: "Forma de pago",
                                          default: "20",
                                        },
                                        {
                                          name: "ats_tratamiento",
                                          label: "Tratamiento ATS",
                                          options: [
                                            {
                                              value: "incluir",
                                              label: "Incluir",
                                            },
                                            {
                                              value: "excluir",
                                              label: "Excluir con evidencia",
                                            },
                                          ],
                                        },
                                        {
                                          name: "ats_motivo",
                                          label: "Motivo de exclusión",
                                          required: false,
                                          span: true,
                                        },
                                        {
                                          name: "no_retencion_motivo",
                                          label:
                                            "Justificación si la compra no tiene retención IR (332)",
                                          required: false,
                                          span: true,
                                        },
                                        {
                                          name: "ats_evidencia_texto",
                                          label:
                                            "Referencia de evidencia de exclusión",
                                          required: false,
                                          span: true,
                                        },
                                        {
                                          name: "unicidad_revisada",
                                          label:
                                            "Revisión de unicidad y requisitos XML para excluir del ATS",
                                          options: [
                                            {
                                              value: "no",
                                              label: "Pendiente de revisión",
                                            },
                                            {
                                              value: "si",
                                              label:
                                                "Revisado y sustentado en la referencia",
                                            },
                                          ],
                                          span: true,
                                        },
                                      ],
                                      initial: {
                                        ...d,
                                        no_retencion_motivo:
                                          d.ats_evidencia
                                            ?.no_retencion_motivo || "",
                                        ats_evidencia_texto:
                                          d.ats_evidencia?.referencia || "",
                                        unicidad_revisada: d.ats_evidencia
                                          ?.unicidad_revisada
                                          ? "si"
                                          : "no",
                                      },
                                      submit: async (v) => {
                                        await post(
                                          `${base}comprobantes/${d.id}/revisar/`,
                                          {
                                            ...v,
                                            ats_evidencia: {
                                              ...d.ats_evidencia,
                                              unicidad_revisada:
                                                v.unicidad_revisada === "si",
                                              ...(v.ats_evidencia_texto
                                                ? {
                                                    referencia:
                                                      v.ats_evidencia_texto,
                                                  }
                                                : {}),
                                              ...(v.no_retencion_motivo
                                                ? {
                                                    no_retencion_motivo:
                                                      v.no_retencion_motivo,
                                                  }
                                                : {}),
                                            },
                                          },
                                        );
                                      },
                                    })
                                  }
                                >
                                  Revisar
                                </button>
                                <button
                                  onClick={() =>
                                    setDialog({
                                      title: "Distribuir comprobante",
                                      content: (
                                        <DistributionForm
                                          doc={d}
                                          base={base}
                                          data={data}
                                          onComplete={() => {
                                            setDialog(null);
                                            setToast("Distribución guardada.");
                                            refresh();
                                          }}
                                        />
                                      ),
                                    })
                                  }
                                >
                                  Distribuir
                                </button>
                                <button
                                  onClick={() =>
                                    run(() =>
                                      post(
                                        `${base}comprobantes/${d.id}/contabilizar/`,
                                        {},
                                      ),
                                    )
                                  }
                                >
                                  Contabilizar
                                </button>
                              </>
                            )}
                            {d.estado === "borrador" && (
                              <button
                                onClick={() =>
                                  reason(
                                    "Descartar borrador",
                                    `comprobantes/${d.id}/descartar`,
                                  )
                                }
                              >
                                Descartar
                              </button>
                            )}
                            {d.estado !== "anulado" && (
                              <button
                                onClick={() =>
                                  reason(
                                    "Anular comprobante",
                                    `comprobantes/${d.id}/anular`,
                                    true,
                                  )
                                }
                              >
                                Anular
                              </button>
                            )}
                          </td>
                        </>
                      )}
                    />
                  </section>
                )}
                {page === "terceros" && (
                  <section className="panel">
                    <Toolbar
                      title="Directorio"
                      search={search}
                      setSearch={setSearch}
                    >
                      <Button
                        secondary
                        onClick={() =>
                          setDialog({
                            title: "Importar terceros",
                            kind: "maestros-terceros",
                          })
                        }
                      >
                        <Upload size={16} />
                        Importar
                      </Button>
                      <Button onClick={newThird}>
                        <Plus size={16} />
                        Nuevo tercero
                      </Button>
                    </Toolbar>
                    <Table
                      columns={[
                        "Nombre / razón social",
                        "Identificación",
                        "Relación",
                        "Correo",
                        "",
                      ]}
                      rows={filtered("terceros")}
                      render={(t) => (
                        <>
                          <td>
                            <strong>{t.nombre}</strong>
                            <small>
                              {t.direccion || "Sin dirección registrada"}
                            </small>
                          </td>
                          <td>
                            <code>{t.identificacion}</code>
                          </td>
                          <td>
                            {t.cliente && <Badge tone="blue">Cliente</Badge>}{" "}
                            {t.proveedor && <Badge>Proveedor</Badge>}
                          </td>
                          <td>{t.email || "—"}</td>
                          <td>
                            <button
                              className="row-link"
                              onClick={() =>
                                setDialog({
                                  title: "Editar tercero",
                                  fields: [
                                    {
                                      name: "nombre",
                                      label: "Nombre",
                                      span: true,
                                    },
                                    {
                                      name: "email",
                                      label: "Correo",
                                      type: "email",
                                      required: false,
                                    },
                                    {
                                      name: "direccion",
                                      label: "Dirección",
                                      required: false,
                                    },
                                  ],
                                  initial: t,
                                  submit: async (v) => {
                                    await api(`${base}terceros/${t.id}/`, {
                                      method: "PATCH",
                                      body: JSON.stringify(v),
                                    });
                                  },
                                })
                              }
                            >
                              Editar
                            </button>
                          </td>
                        </>
                      )}
                    />
                  </section>
                )}
                {page === "cartera" && (
                  <Portfolio
                    base={base}
                    start={start}
                    end={end}
                    revision={revision}
                    onPayment={() =>
                      setDialog({
                        title: "Registrar cobro o pago",
                        kind: "pago",
                      })
                    }
                    onRetention={() =>
                      setDialog({
                        title: "Registrar retención",
                        kind: "retencion",
                      })
                    }
                    onDoc={showDoc}
                    onAdvance={() =>
                      form(
                        "Aplicar anticipo",
                        [
                          {
                            name: "movimiento",
                            label: "Anticipo",
                            options: options(
                              "movimientos",
                              (r) => `${r.referencia} · ${fmt(r.importe)}`,
                            ),
                          },
                          {
                            name: "partida",
                            label: "ID de partida",
                            type: "number",
                          },
                          { name: "importe", label: "Importe" },
                          {
                            name: "fecha",
                            label: "Fecha",
                            type: "date",
                            default: start,
                          },
                        ],
                        "tesoreria/aplicar_anticipo",
                      )
                    }
                  />
                )}
                {page === "bancos" && (
                  <Banks
                    base={base}
                    data={data}
                    start={start}
                    end={end}
                    revision={revision}
                    run={run}
                    onImport={() =>
                      setDialog({
                        title: "Importar extracto bancario",
                        kind: "extracto",
                      })
                    }
                    onPayment={() =>
                      setDialog({ title: "Registrar movimiento", kind: "pago" })
                    }
                    onTransfer={() =>
                      form(
                        "Transferencia entre cuentas",
                        [
                          {
                            name: "origen",
                            label: "Cuenta de origen",
                            options: bankOptions,
                          },
                          {
                            name: "destino",
                            label: "Cuenta de destino",
                            options: bankOptions,
                          },
                          { name: "importe", label: "Importe" },
                          {
                            name: "fecha",
                            label: "Fecha",
                            type: "date",
                            default: start,
                          },
                          { name: "concepto", label: "Concepto", span: true },
                        ],
                        "tesoreria/transferir",
                      )
                    }
                    onReconcile={() =>
                      setDialog({
                        title: "Conciliar partidas",
                        kind: "conciliacion",
                      })
                    }
                    onBank={() =>
                      form(
                        "Nueva cuenta de tesorería",
                        [
                          { name: "nombre", label: "Nombre" },
                          {
                            name: "tipo",
                            label: "Tipo",
                            options: [
                              { value: "banco", label: "Banco" },
                              { value: "caja", label: "Caja" },
                            ],
                            default: "banco",
                          },
                          { name: "banco", label: "Banco", required: false },
                          {
                            name: "numero",
                            label: "Número de cuenta",
                            required: false,
                          },
                          {
                            name: "cuenta",
                            label: "Cuenta contable",
                            options: accountOptions,
                            searchable: true,
                            span: true,
                          },
                        ],
                        "tesoreria",
                      )
                    }
                  />
                )}
                {page === "ats" && (
                  <section className="panel">
                    <div className="panel-heading">
                      <div>
                        <h2>Generaciones del período</h2>
                        <p>
                          Cada exportación conserva sus documentos y reglas
                          aplicadas.
                        </p>
                      </div>
                      <Button
                        onClick={() =>
                          run(
                            () =>
                              post(`${base}ats/`, { periodo: Number(period) }),
                            "ATS generado. Revisa el resultado de las validaciones.",
                          )
                        }
                      >
                        <Plus size={16} />
                        Generar ATS
                      </Button>
                    </div>
                    <div className="notice">
                      <ShieldCheck size={19} />
                      <div>
                        <strong>Validación local y presentación manual</strong>
                        <p>
                          Revisamos estructura, bases, retenciones, agrupación y
                          formas de pago. El catálogo de servicios está
                          contrastado con las tablas SRI de agosto de 2026. La
                          validación en DIMM y la presentación al SRI siguen
                          siendo pasos manuales.
                        </p>
                      </div>
                    </div>
                    <Table
                      columns={[
                        "Generación",
                        "Documentos",
                        "Validación",
                        "Presentación",
                        "Acciones",
                      ]}
                      rows={(data.ats || []).filter(
                        (a) => a.periodo === Number(period),
                      )}
                      render={(a) => (
                        <>
                          <td>
                            <strong>
                              ATS · {MONTHS[month - 1]} {year}
                            </strong>
                            <small>
                              #{a.id} ·{" "}
                              {new Date(a.creado).toLocaleString("es-EC")}
                            </small>
                          </td>
                          <td>
                            {a.resumen.incluidos} incluidos
                            <small>{a.resumen.excluidos} excluidos</small>
                          </td>
                          <td>
                            <Badge tone={a.errores.length ? "amber" : "green"}>
                              {a.errores.length
                                ? `${a.errores.length} observaciones`
                                : "XSD válido"}
                            </Badge>
                          </td>
                          <td>
                            <Badge>{a.estado}</Badge>
                          </td>
                          <td className="row-actions">
                            <button
                              onClick={async () => {
                                try {
                                  const ds = await api(
                                    `${base}ats/${a.id}/documentos/`,
                                  );
                                  setDetail({ ats: a, documentos: ds });
                                } catch (e) {
                                  setError((e as Error).message);
                                }
                              }}
                            >
                              Revisar
                            </button>
                            {!a.errores.length && (
                              <a href={`/api/v1/${base}ats/${a.id}/descargar/`}>
                                ZIP <Download size={13} />
                              </a>
                            )}
                            <a href={`/api/v1/${base}ats/${a.id}/control/`}>
                              Informe de control <Download size={13} />
                            </a>
                            <button
                              onClick={() =>
                                form(
                                  "Registrar presentación al SRI",
                                  [
                                    {
                                      name: "estado",
                                      label: "Estado",
                                      options: [
                                        "presentado",
                                        "aceptado",
                                        "rechazado",
                                      ].map((v) => ({ value: v, label: v })),
                                    },
                                    {
                                      name: "evidencia",
                                      label: "Referencia o mensaje del SRI",
                                      span: true,
                                    },
                                  ],
                                  `ats/${a.id}/presentacion`,
                                )
                              }
                            >
                              Presentación
                            </button>
                          </td>
                        </>
                      )}
                    />
                  </section>
                )}
                {page === "analitica" && (
                  <AnaliticaPage
                    key={company}
                    data={data}
                    base={base}
                    onChanged={refresh}
                    tab={analyticsTab}
                    onTab={setAnalyticsTab}
                    onReport={(kind, id) => {
                      setReportKind(kind);
                      setReportCenter(
                        kind === "resultados-centros" ? id || "" : "",
                      );
                      setReportProject(
                        kind === "resultados-proyectos" ? id || "" : "",
                      );
                      setReportAccount("");
                      setReportThird("");
                      navigate("reportes");
                    }}
                  />
                )}
                {page === "reportes" && (
                  <section className="panel">
                    <div className="panel-heading">
                      <label className="report-select">
                        <span>Reporte</span>
                        <select
                          value={reportKind}
                          aria-label="Reporte"
                          onChange={(e) => {
                            setReportKind(e.target.value);
                            setReportCenter("");
                            setReportProject("");
                          }}
                        >
                          {Object.entries({
                            balance: "Balance de comprobación",
                            situacion: "Estado de situación financiera",
                            resultados: "Estado de resultados",
                            "resultados-centros":
                              "Resultados por centro de costo",
                            "resultados-proyectos": "Resultados por proyecto",
                            "resultados-analiticos":
                              "Resultados por centro y proyecto",
                            "detalle-analitico":
                              "Detalle de ingresos, costos y gastos",
                            diario: "Libro diario",
                            mayor: "Mayor contable",
                            auxiliares: "Auxiliares de terceros",
                            cartera: "Cartera y vencimientos",
                            bancos: "Bancos y conciliación",
                            impuestos: "Resumen de IVA",
                            retenciones: "Resumen de retenciones",
                          }).map(([v, l]) => (
                            <option value={v} key={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="button-group">
                        {["csv", "xlsx"].map((f) => (
                          <a
                            className="btn secondary"
                            key={f}
                            href={`/api/v1/${base}reportes/${reportKind}/?desde=${start}&hasta=${end}&formato=${f}&cuenta=${reportAccount}&tercero=${reportThird}&centro_costo=${reportCenter}&proyecto=${reportProject}`}
                          >
                            <Download size={15} />
                            {f.toUpperCase()}
                          </a>
                        ))}
                      </div>
                    </div>
                    {[
                      "resultados",
                      "diario",
                      "mayor",
                      "auxiliares",
                      "resultados-centros",
                      "resultados-proyectos",
                      "resultados-analiticos",
                      "detalle-analitico",
                    ].includes(reportKind) && (
                      <div className="form-grid analytics-filters">
                        <label>
                          <span>Centro de costo (incluye subcentros)</span>
                          <select
                            aria-label="Filtrar por centro de costo"
                            value={reportCenter}
                            onChange={(e) => setReportCenter(e.target.value)}
                          >
                            <option value="">Todos los centros</option>
                            <option value="sin_asignar">Sin asignar</option>
                            {(data["centros-costo"] || []).map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.codigo} · {c.nombre}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Proyecto</span>
                          <select
                            aria-label="Filtrar por proyecto"
                            value={reportProject}
                            onChange={(e) => setReportProject(e.target.value)}
                          >
                            <option value="">Todos los proyectos</option>
                            <option value="sin_asignar">Sin asignar</option>
                            {(data.proyectos || []).map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.codigo} · {p.nombre}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}
                    <DynamicTable
                      rows={rows}
                      onDoc={showDoc}
                      onEntry={showEntry}
                    />
                  </section>
                )}
                {page === "auditoria" && (
                  <section className="panel">
                    <Toolbar
                      title="Registro de auditoría"
                      search={search}
                      setSearch={setSearch}
                    />
                    <Table
                      columns={[
                        "Fecha",
                        "Acción",
                        "Entidad",
                        "Registro",
                        "Detalle",
                      ]}
                      rows={filtered("auditoria")}
                      render={(a) => (
                        <>
                          <td>{new Date(a.creado).toLocaleString("es-EC")}</td>
                          <td>
                            <Badge>{a.accion.replaceAll("_", " ")}</Badge>
                          </td>
                          <td>{a.entidad}</td>
                          <td>#{a.objeto_id}</td>
                          <td className="muted">{JSON.stringify(a.detalle)}</td>
                        </>
                      )}
                    />
                  </section>
                )}
                {page === "configuracion" && (
                  <>
                    <section className="panel">
                      <div className="panel-heading">
                        <div>
                          <h2>Empresas y acceso</h2>
                          <p>Información independiente por RUC.</p>
                        </div>
                        <div className="button-group">
                          <Button
                            secondary
                            onClick={() => run(backup, "Respaldo descargado.")}
                          >
                            <Download size={16} />
                            Respaldar
                          </Button>
                          {session.administrador && (
                            <Button onClick={newCompany}>
                              <Plus size={16} />
                              Nueva empresa
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="company-info">
                        <Building2 size={28} />
                        <div>
                          <strong>{active?.razon_social}</strong>
                          <p>
                            RUC {active?.ruc} ·{" "}
                            {active?.direccion || "Dirección pendiente"}
                          </p>
                        </div>
                        <Badge tone="green">USD</Badge>
                      </div>
                    </section>
                    <section className="panel">
                      <div className="panel-heading">
                        <div>
                          <h2>Control de períodos</h2>
                          <p>
                            El cierre bloquea nuevos movimientos. La reapertura
                            conserva el motivo.
                          </p>
                        </div>
                        <Button
                          secondary
                          onClick={() =>
                            form(
                              "Crear ejercicio",
                              [
                                {
                                  name: "anio",
                                  label: "Año",
                                  default: String(year + 1),
                                },
                              ],
                              `ejercicios/crear`,
                              (v) => v,
                            )
                          }
                        >
                          <Plus size={15} />
                          Ejercicio
                        </Button>
                      </div>
                      <Table
                        columns={["Período", "Estado", "Acción"]}
                        rows={data.periodos || []}
                        render={(p) => (
                          <>
                            <td>
                              {MONTHS[p.mes - 1]}{" "}
                              {
                                data.ejercicios?.find(
                                  (e) => e.id === p.ejercicio,
                                )?.anio
                              }
                            </td>
                            <td>
                              <Badge tone={p.cerrado ? "neutral" : "green"}>
                                {p.cerrado ? "Cerrado" : "Abierto"}
                              </Badge>
                            </td>
                            <td>
                              <button
                                className="row-link"
                                onClick={() =>
                                  reason(
                                    p.cerrado
                                      ? "Reabrir período"
                                      : "Cerrar período",
                                    `periodos/${p.id}/${p.cerrado ? "reabrir" : "cerrar"}`,
                                  )
                                }
                              >
                                {p.cerrado ? (
                                  <RotateCcw size={14} />
                                ) : (
                                  <LockKeyhole size={14} />
                                )}{" "}
                                {p.cerrado ? "Reabrir" : "Cerrar"}
                              </button>
                            </td>
                          </>
                        )}
                      />
                    </section>
                    <section className="panel">
                      <div className="panel-heading">
                        <div>
                          <h2>Perfil tributario</h2>
                          <p>
                            La condición de agente de retención se configura
                            según su vigencia.
                          </p>
                        </div>
                        <Button
                          secondary
                          onClick={() =>
                            form(
                              "Nuevo perfil tributario",
                              [
                                {
                                  name: "desde",
                                  label: "Desde",
                                  type: "date",
                                  default: start,
                                },
                                {
                                  name: "hasta",
                                  label: "Hasta",
                                  type: "date",
                                  required: false,
                                },
                                {
                                  name: "agente_retencion",
                                  label: "Agente de retención",
                                  options: [
                                    { value: "si", label: "Sí" },
                                    { value: "no", label: "No" },
                                  ],
                                  default: "no",
                                },
                                {
                                  name: "fuente",
                                  label: "Referencia / resolución",
                                },
                              ],
                              "perfiles",
                              (v) => ({
                                ...v,
                                hasta: v.hasta || null,
                                agente_retencion: v.agente_retencion === "si",
                              }),
                            )
                          }
                        >
                          <Plus size={15} />
                          Perfil
                        </Button>
                      </div>
                      <Table
                        columns={[
                          "Vigencia",
                          "Régimen",
                          "Agente de retención",
                          "Fuente",
                        ]}
                        rows={data.perfiles || []}
                        render={(p) => (
                          <>
                            <td>
                              {p.desde} → {p.hasta || "Sin fin"}
                            </td>
                            <td>{p.regimen}</td>
                            <td>{p.agente_retencion ? "Sí" : "No"}</td>
                            <td>{p.fuente}</td>
                          </>
                        )}
                      />
                    </section>
                    <section className="panel">
                      <div className="panel-heading">
                        <div>
                          <h2>Usuarios y permisos</h2>
                          <p>El acceso se asigna por empresa.</p>
                        </div>
                        {session.administrador && (
                          <Button
                            secondary
                            onClick={() =>
                              form(
                                "Nuevo usuario local",
                                [
                                  { name: "usuario", label: "Usuario" },
                                  {
                                    name: "password",
                                    label: "Contraseña (mínimo 12 caracteres)",
                                    type: "password",
                                  },
                                  {
                                    name: "rol",
                                    label: "Rol",
                                    options: [
                                      "administrador",
                                      "contador",
                                      "consulta",
                                    ].map((r) => ({ value: r, label: r })),
                                    default: "contador",
                                  },
                                ],
                                "usuarios-locales",
                              )
                            }
                          >
                            <Plus size={15} />
                            Usuario local
                          </Button>
                        )}
                      </div>
                      <Table
                        columns={["Usuario", "Rol", ""]}
                        rows={data.miembros || []}
                        render={(m) => (
                          <>
                            <td>Usuario #{m.usuario}</td>
                            <td>
                              <Badge>{m.rol}</Badge>
                            </td>
                            <td>
                              <button
                                className="row-link"
                                onClick={() =>
                                  setDialog({
                                    title: "Editar permiso",
                                    fields: [
                                      {
                                        name: "rol",
                                        label: "Rol",
                                        options: [
                                          "administrador",
                                          "contador",
                                          "consulta",
                                        ].map((r) => ({ value: r, label: r })),
                                      },
                                    ],
                                    initial: { rol: m.rol },
                                    submit: async (v) => {
                                      await api(`${base}miembros/${m.id}/`, {
                                        method: "PATCH",
                                        body: JSON.stringify(v),
                                      });
                                    },
                                  })
                                }
                              >
                                Cambiar rol
                              </button>
                            </td>
                          </>
                        )}
                      />
                    </section>
                    <section className="panel">
                      <div className="panel-heading">
                        <div>
                          <h2>Cuentas predeterminadas</h2>
                          <p>
                            Reglas de contabilización de documentos y
                            movimientos.
                          </p>
                        </div>
                      </div>
                      <Table
                        columns={["Regla", "Cuenta contable", ""]}
                        rows={data.reglas || []}
                        render={(r) => (
                          <>
                            <td>{r.clave.replaceAll("_", " ")}</td>
                            <td>
                              {
                                (data.cuentas || []).find(
                                  (c) => c.id === r.cuenta,
                                )?.nombre
                              }
                            </td>
                            <td>
                              <button
                                className="row-link"
                                onClick={() =>
                                  setDialog({
                                    title: "Cambiar cuenta predeterminada",
                                    fields: [
                                      {
                                        name: "cuenta",
                                        label: r.clave,
                                        options: accountOptions,
                                        searchable: true,
                                        span: true,
                                      },
                                    ],
                                    initial: { cuenta: r.cuenta },
                                    submit: async (v) => {
                                      await api(`${base}reglas/${r.id}/`, {
                                        method: "PATCH",
                                        body: JSON.stringify(v),
                                      });
                                    },
                                  })
                                }
                              >
                                Cambiar
                              </button>
                            </td>
                          </>
                        )}
                      />
                    </section>
                  </>
                )}
              </>
            )
          )}
          <footer className="page-footer">
            <span>
              contable <b>·</b> Ecuador
            </span>
            <span>
              <ShieldCheck size={13} /> Tu información, con acceso privado
            </span>
          </footer>
        </div>
      </main>
      {toast && (
        <div className="toast" role="status">
          <CheckCircle2 size={18} />
          {toast}
        </div>
      )}
      {dialog && (
        <Modal
          title={dialog.title}
          subtitle={dialog.subtitle}
          onClose={() => setDialog(null)}
        >
          {dialog.content || dialog.kind ? (
            <>
              {dialog.content || (
                <WorkflowForm
                  kind={dialog.kind!}
                  base={base}
                  data={data}
                  start={start}
                  end={end}
                  period={period}
                  onComplete={() => {
                    setDialog(null);
                    setToast("Operación completada.");
                    refresh();
                  }}
                />
              )}
            </>
          ) : (
            <SimpleForm
              fields={dialog.fields || []}
              initial={dialog.initial}
              onSubmit={async (v) => {
                await dialog.submit!(v);
                setDialog(null);
                setToast("Operación completada.");
                refresh();
              }}
            />
          )}
        </Modal>
      )}
      {selectedAccount?.scope === planScope && page === "cuentas" && (
        <Modal
          title={`${selectedAccount.codigo} · ${selectedAccount.nombre}`}
          subtitle={`Movimientos contabilizados del ${start} al ${end}${!selectedAccount.movimiento ? " · Incluye subcuentas" : ""}`}
          onClose={() => setSelectedAccount(null)}
        >
          <Table
            columns={["Saldo inicial", "Debe", "Haber", "Saldo final"]}
            rows={[selectedAccount]}
            render={(r) => (
              <>
                {["inicial", "debe", "haber", "saldo"].map((field) => (
                  <td className="amount" key={field}>
                    {fmt(r[field])}
                  </td>
                ))}
              </>
            )}
          />
          <p className="account-period">
            Saldos del resumen según naturaleza. En los movimientos, D indica
            saldo deudor y A, acreedor.
          </p>
          {accountLedgerError ? (
            <div className="alert error" role="alert">
              {accountLedgerError}
            </div>
          ) : accountLedger === null ? (
            <p role="status">Cargando movimientos…</p>
          ) : accountLedger.length ? (
            <Table
              columns={[
                "Fecha",
                "Cuenta",
                "Asiento",
                "Concepto",
                "Tercero",
                "Debe",
                "Haber",
                "Saldo D/A",
              ]}
              rows={accountLedger}
              render={(r) => (
                <>
                  <td>{r.fecha}</td>
                  <td>
                    {r.cuenta} · {r.nombre}
                  </td>
                  <td>
                    <button
                      className="row-link"
                      onClick={() => {
                        setSelectedAccount(null);
                        showEntry(r.asiento);
                      }}
                    >
                      {r.numero}
                    </button>
                  </td>
                  <td>{r.concepto}</td>
                  <td>{r.tercero || "—"}</td>
                  <td className="amount">{fmt(r.debe)}</td>
                  <td className="amount">{fmt(r.haber)}</td>
                  <td className="amount">
                    {fmt(Math.abs(Number(r.saldo)))}{" "}
                    {Number(r.saldo) < 0 ? "A" : "D"}
                  </td>
                </>
              )}
            />
          ) : (
            <p>
              Esta cuenta no tiene movimientos contabilizados en el período
              seleccionado.
            </p>
          )}
        </Modal>
      )}
      {detail && (
        <Modal
          title={
            detail.comprobante
              ? "Detalle del comprobante"
              : detail.asiento
                ? "Detalle del asiento"
                : "Revisión del ATS"
          }
          onClose={() => setDetail(null)}
        >
          {detail.comprobante && (
            <>
              <div className="detail-summary">
                <strong>{detail.comprobante.concepto}</strong>
                <p>
                  {detail.comprobante.tercero_nombre} ·{" "}
                  {detail.comprobante.fecha_emision}
                </p>
                <h2>{fmt(detail.comprobante.total)}</h2>
                {detail.comprobante.asiento && (
                  <button
                    className="row-link"
                    onClick={() => showEntry(detail.comprobante.asiento)}
                  >
                    Ver asiento #{detail.comprobante.asiento}{" "}
                    <ArrowUpRight size={14} />
                  </button>
                )}
              </div>
              <DynamicTable rows={detail.detalles} />
              <h3>Distribución por centro de costo y proyecto</h3>
              {detail.distribucion?.length ? (
                <DynamicTable rows={detail.distribucion} />
              ) : (
                <p>Sin asignación analítica.</p>
              )}
              <h3>Impuestos</h3>
              <DynamicTable rows={detail.impuestos} />
              {detail.comprobante.archivo && (
                <a
                  className="btn secondary"
                  href={`/api/v1/${base}archivos/${detail.comprobante.archivo}/descargar/`}
                >
                  <Download size={15} />
                  Archivo original
                </a>
              )}
            </>
          )}
          {detail.asiento && (
            <>
              <p>{detail.asiento.concepto}</p>
              <DynamicTable rows={detail.lineas} />
            </>
          )}
          {detail.ats && (
            <>
              <div className="notice">
                <FileCheck2 size={20} />
                <div>
                  <strong>
                    {detail.ats.resumen.xsd_valido
                      ? "XML válido según XSD oficial"
                      : "XML con observaciones"}
                  </strong>
                  <p>
                    Validación oficial: pendiente ·{" "}
                    {detail.ats.resumen.catalogo}
                  </p>
                  {detail.ats.resumen.reglas_revision && (
                    <p>
                      Bases físicas reportadas:{" "}
                      {fmt(detail.ats.resumen.total_ventas)}. El informe de
                      control conserva importes, decisiones y fuentes de esta
                      generación.
                    </p>
                  )}
                </div>
              </div>
              {detail.ats.errores.map((e: Row, i: number) => (
                <div className="alert error" key={i}>
                  {e.comprobante && (
                    <button
                      className="row-link"
                      onClick={() => showDoc(e.comprobante)}
                    >
                      Comprobante #{e.comprobante}
                    </button>
                  )}{" "}
                  {e.mensaje}
                </div>
              ))}
              <DynamicTable rows={detail.documentos} onDoc={showDoc} />
              <details>
                <summary>Ver XML generado</summary>
                <pre>{detail.ats.xml}</pre>
              </details>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  icon,
  note,
  accent = false,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  note: string;
  accent?: boolean;
}) {
  return (
    <div className={`metric ${accent ? "accent" : ""}`}>
      <div className="metric-label">
        {label}
        <span>{icon}</span>
      </div>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  );
}
function Checklist({
  done,
  title,
  sub,
  onClick,
}: {
  done: boolean;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button className="checklist" onClick={onClick}>
      {done ? (
        <CheckCircle2 size={20} className="completed" />
      ) : (
        <Circle size={20} />
      )}
      <span>
        <strong>{title}</strong>
        <small>{sub}</small>
      </span>
      <ChevronRight size={14} />
    </button>
  );
}
function Toolbar({
  title,
  search,
  setSearch,
  children,
}: {
  title: string;
  search: string;
  setSearch: (s: string) => void;
  children?: ReactNode;
}) {
  return (
    <div className="toolbar">
      <h2>{title}</h2>
      <div className="toolbar-actions">
        <label className="search">
          <Search size={15} />
          <input
            placeholder="Buscar…"
            aria-label="Buscar registros"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {children}
      </div>
    </div>
  );
}
function Modal({
  title,
  subtitle,
  children,
  onClose,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}
function SimpleForm({
  fields,
  initial = {},
  onSubmit,
}: {
  fields: Field[];
  initial?: Row;
  onSubmit: (r: Row) => Promise<void>;
}) {
  const [v, setV] = useState<Row>({
    ...Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])),
    ...initial,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await onSubmit(v);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit}>
      <div className="form-grid">
        {fields.map((f) => (
          <FieldInput
            key={f.name}
            f={f}
            value={v[f.name]}
            onChange={(value) => setV({ ...v, [f.name]: value })}
          />
        ))}
      </div>
      {err && (
        <div className="alert error" role="alert">
          {err}
        </div>
      )}
      <div className="form-footer">
        <span>Los campos con * son obligatorios</span>
        <Button type="submit" disabled={busy}>
          {busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />}
          Guardar
        </Button>
      </div>
    </form>
  );
}
function DynamicTable({
  rows,
  onDoc,
  onEntry,
}: {
  rows: Row[];
  onDoc?: (id: any) => void;
  onEntry?: (id: any) => void;
}) {
  const keys = rows.length
    ? Object.keys(rows[0]).filter(
        (k) =>
          ![
            "empresa",
            "creado",
            "empresa_id",
            "cuenta_id",
            "tercero_id",
            "centro_costo_id",
            "proyecto_id",
            "version",
          ].includes(k) &&
          !(
            ["centro_costo", "proyecto"].includes(k) && `${k}_nombre` in rows[0]
          ),
      )
    : [];
  return (
    <Table
      columns={keys.map((k) => k.replaceAll("_", " "))}
      rows={rows}
      render={(r) => (
        <>
          {keys.map((k) => (
            <td key={k}>
              {k === "comprobante" && r[k] && onDoc ? (
                <button className="row-link" onClick={() => onDoc(r[k])}>
                  #{r[k]}
                </button>
              ) : k === "asiento" && r[k] && onEntry ? (
                <button className="row-link" onClick={() => onEntry(r[k])}>
                  #{r[k]}
                </button>
              ) : typeof r[k] === "boolean" ? (
                r[k] ? (
                  "Sí"
                ) : (
                  "No"
                )
              ) : typeof r[k] === "object" ? (
                JSON.stringify(r[k])
              ) : (
                String(r[k] ?? "—")
              )}
            </td>
          ))}
        </>
      )}
    />
  );
}
function Login({
  onLogin,
  error,
  setError,
}: {
  onLogin: (r: Row) => void;
  error: string;
  setError: (s: string) => void;
}) {
  const [user, setUser] = useState("admin");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onLogin(await post("sesion/", { usuario: user, password }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-layout">
      <div className="login-art">
        <div className="brand">
          <div className="brand-symbol">
            <BookOpen />
          </div>
          <div>
            contable<span>ECUADOR</span>
          </div>
        </div>
        <div>
          <div className="eyebrow">CONTABILIDAD CON CLARIDAD</div>
          <h1>
            Cada movimiento.
            <br />
            Todo en orden.
          </h1>
          <p>
            La contabilidad de tus empresas,
            <br />
            en un solo lugar y bajo tu control.
          </p>
          <div className="login-tags">
            <span>
              <ShieldCheck size={16} /> Acceso seguro
            </span>
            <span>
              <Building2 size={16} /> Varias empresas
            </span>
          </div>
        </div>
        <small>Hecho para trabajar en Ecuador.</small>
      </div>
      <div className="login-content">
        <form onSubmit={submit}>
          <Badge tone="green">
            <span className="status-dot" /> ESPACIO PRIVADO
          </Badge>
          <h2>Bienvenido a Contable</h2>
          <p>Ingresa para continuar con tu espacio de trabajo.</p>
          <label>
            <span>Usuario</span>
            <input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <label>
            <span>Contraseña</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {error && (
            <div className="alert error" role="alert">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy}>
            {busy ? (
              <Loader2 size={17} className="spin" />
            ) : (
              "Ingresar al sistema"
            )}
            <ArrowUpRight size={17} />
          </Button>
          <div className="login-hint">
            <ShieldCheck size={18} />
            <span>
              Acceso privado a la contabilidad de tus empresas.
              <small>Ingresa con las credenciales de tu administrador.</small>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}

function Portfolio({
  base,
  start,
  end,
  revision,
  onPayment,
  onRetention,
  onDoc,
  onAdvance,
}: {
  base: string;
  start: string;
  end: string;
  revision: number;
  onPayment: () => void;
  onRetention: () => void;
  onDoc: (id: any) => void;
  onAdvance: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    api(`${base}reportes/cartera/?desde=${start}&hasta=${end}`)
      .then(setRows)
      .catch((e) => setErr(e.message));
  }, [base, start, end, revision]);
  const sum = (k: string) =>
    rows
      .filter((r) => r.tipo === k)
      .reduce((s, r) => s + cent(r.pendiente), 0n);
  return (
    <>
      <div className="metrics three">
        <Metric
          label="Pendiente por cobrar"
          value={fmt(decimal(sum("cobrar")))}
          icon={<ArrowUpRight size={18} />}
          note="Facturas menos liquidaciones"
          accent
        />
        <Metric
          label="Pendiente por pagar"
          value={fmt(decimal(sum("pagar")))}
          icon={<ArrowLeftRight size={18} />}
          note="Obligaciones con proveedores"
        />
        <Metric
          label="Partidas vencidas"
          value={String(rows.filter((r) => r.dias_vencido > 0).length)}
          icon={<CalendarDays size={18} />}
          note="Según la fecha de corte"
        />
      </div>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Cartera pendiente</h2>
            <p>Fecha de corte: {end}</p>
          </div>
          <div className="button-group">
            <Button secondary onClick={onAdvance}>
              Aplicar anticipo
            </Button>
            <Button secondary onClick={onRetention}>
              <FileCheck2 size={16} />
              Retención
            </Button>
            <Button onClick={onPayment}>
              <Plus size={16} />
              Cobro o pago
            </Button>
          </div>
        </div>
        {err && <div className="alert error">{err}</div>}
        <Table
          columns={[
            "Partida",
            "Tercero",
            "Tipo",
            "Vencimiento",
            "Pendiente",
            "Documento",
          ]}
          rows={rows}
          render={(r) => (
            <>
              <td>#{r.id}</td>
              <td>
                <strong>{r.tercero}</strong>
              </td>
              <td>
                <Badge tone={r.tipo === "cobrar" ? "blue" : "neutral"}>
                  Por {r.tipo}
                </Badge>
              </td>
              <td>
                {r.vencimiento}
                <small>
                  {r.dias_vencido > 0
                    ? `${r.dias_vencido} días vencido`
                    : "Dentro del plazo"}
                </small>
              </td>
              <td className="amount">{fmt(r.pendiente)}</td>
              <td>
                <button
                  className="row-link"
                  onClick={() => onDoc(r.comprobante)}
                >
                  #{r.comprobante}
                  <ArrowUpRight size={14} />
                </button>
              </td>
            </>
          )}
        />
      </section>
    </>
  );
}
function Banks({
  base,
  data,
  start,
  end,
  revision,
  run,
  onImport,
  onPayment,
  onTransfer,
  onReconcile,
  onBank,
}: {
  base: string;
  data: Record<string, Row[]>;
  start: string;
  end: string;
  revision: number;
  run: (fn: () => Promise<any>, msg?: string) => Promise<void>;
  onImport: () => void;
  onPayment: () => void;
  onTransfer: () => void;
  onReconcile: () => void;
  onBank: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [suggestions, setSuggestions] = useState<Row[]>([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    api(`${base}reportes/bancos/?desde=${start}&hasta=${end}`)
      .then(setRows)
      .catch((e) => setErr(e.message));
  }, [base, start, end, revision]);
  return (
    <>
      <div className="bank-cards">
        {rows.map((r) => (
          <div className="bank-card" key={r.id}>
            <Landmark size={21} />
            <div>
              <strong>{r.nombre}</strong>
              <small>Saldo en libros</small>
              <h2>{fmt(r.saldo_contable)}</h2>
            </div>
            <Badge
              tone={r.diferencia_sin_explicar === "0.00" ? "green" : "amber"}
            >
              {r.saldo_banco === null
                ? "Sin extracto"
                : r.diferencia_sin_explicar === "0.00"
                  ? "Sin diferencia"
                  : "Revisar"}
            </Badge>
            <div className="bank-card-footer">
              <span>
                Saldo banco{" "}
                <b>{r.saldo_banco === null ? "—" : fmt(r.saldo_banco)}</b>
              </span>
              <span>
                Sin explicar{" "}
                <b>
                  {r.diferencia_sin_explicar === null
                    ? "—"
                    : fmt(r.diferencia_sin_explicar)}
                </b>
              </span>
            </div>
          </div>
        ))}
      </div>
      {err && <div className="alert error">{err}</div>}
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Movimientos de tesorería</h2>
            <p>Ingresos, egresos y transferencias contabilizados.</p>
          </div>
          <div className="button-group">
            <Button secondary onClick={onBank}>
              <Plus size={15} />
              Cuenta
            </Button>
            <Button secondary onClick={onTransfer}>
              <ArrowLeftRight size={15} />
              Transferir
            </Button>
            <Button onClick={onPayment}>
              <Plus size={15} />
              Movimiento
            </Button>
          </div>
        </div>
        <Table
          columns={["Fecha", "Referencia", "Concepto", "Importe", "Asiento"]}
          rows={(data.movimientos || []).filter(
            (r) => r.fecha >= start && r.fecha <= end,
          )}
          render={(m) => (
            <>
              <td>{m.fecha}</td>
              <td>
                <code>{m.referencia}</code>
              </td>
              <td>{m.concepto}</td>
              <td
                className={`amount ${Number(m.importe) > 0 ? "positive" : ""}`}
              >
                {fmt(m.importe)}
              </td>
              <td>#{m.asiento}</td>
            </>
          )}
        />
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Conciliación bancaria</h2>
            <p>Las sugerencias se aplican únicamente al confirmarlas.</p>
          </div>
          <div className="button-group">
            <Button secondary onClick={onImport}>
              <Upload size={15} />
              Importar extracto
            </Button>
            <Button onClick={onReconcile}>
              <ArrowLeftRight size={15} />
              Conciliar partidas
            </Button>
          </div>
        </div>
        <div className="bank-select">
          {(data.tesoreria || [])
            .filter((t) => t.tipo === "banco")
            .map((t) => (
              <Button
                secondary
                key={t.id}
                onClick={() =>
                  api(`${base}tesoreria/${t.id}/sugerencias/`)
                    .then(setSuggestions)
                    .catch((e) => setErr(e.message))
                }
              >
                Buscar coincidencias · {t.nombre}
              </Button>
            ))}
        </div>
        <Table
          columns={[
            "Línea del extracto",
            "Movimiento",
            "Importe",
            "Coincidencia",
            "",
          ]}
          rows={suggestions}
          render={(s) => (
            <>
              <td>#{s.linea}</td>
              <td>#{s.movimiento}</td>
              <td>{fmt(s.importe)}</td>
              <td>
                {s.referencia_coincide ? "Referencia e importe" : "Importe"} ·{" "}
                {s.dias} días
              </td>
              <td>
                <button
                  className="row-link"
                  onClick={() =>
                    run(async () => {
                      const m = data.movimientos.find(
                        (m) => m.id === s.movimiento,
                      )!;
                      await post(`${base}conciliaciones/`, {
                        tesoreria: m.tesoreria,
                        fecha: end,
                        asignaciones: [
                          {
                            linea: s.linea,
                            movimiento: s.movimiento,
                            importe: s.importe,
                          },
                        ],
                      });
                      setSuggestions((v) => v.filter((x) => x !== s));
                    }, "Coincidencia conciliada.")
                  }
                >
                  Confirmar
                </button>
              </td>
            </>
          )}
        />
        <div className="panel-footer">
          {(data.extractos || []).length} extractos importados ·{" "}
          {(data["extracto-lineas"] || []).length} movimientos bancarios
        </div>
      </section>
    </>
  );
}
function WorkflowForm({
  kind,
  base,
  data,
  start,
  end,
  onComplete,
}: {
  kind: string;
  base: string;
  data: Record<string, Row[]>;
  start: string;
  end: string;
  period: string;
  onComplete: () => void;
}) {
  const [values, setValues] = useState<Row>({
    fecha: start,
    fecha_emision: start,
    fecha_registro: kind === "xml" ? end : start,
    fecha_vencimiento: end,
    tipo: "01",
    operacion: "venta",
    secuencial: "",
    base: "100.00",
    tasa: "15",
    direccion: "ingreso",
    importe: "100.00",
    saldo_inicial: "0.00",
    concepto: "",
    referencia: "",
    sustento: "01",
    autorizacion: "",
    establecimiento: "001",
    punto_emision: "001",
    forma_pago: "20",
  });
  const [lines, setLines] = useState<Row[]>(
    kind === "asiento"
      ? [
          { cuenta: "", debe: "0.00", haber: "0.00" },
          { cuenta: "", debe: "0.00", haber: "0.00" },
        ]
      : kind === "retencion"
        ? [
            {
              impuesto: "1",
              codigo: "303",
              base: "100.00",
              porcentaje: "10",
              valor: "10.00",
            },
          ]
        : [],
  );
  const [files, setFiles] = useState<File[]>([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Row[] | null>(null);
  const [result, setResult] = useState<Row[] | null>(null);
  const [portfolio, setPortfolio] = useState<Row[]>([]);
  const [allocations, setAllocations] = useState<Row[]>([]);
  const [mapping, setMapping] = useState<Row>({
    fecha: "fecha",
    referencia: "referencia",
    descripcion: "descripcion",
    debito: "debito",
    credito: "credito",
    saldo: "saldo",
  });
  useEffect(() => {
    if (kind === "pago")
      api(`${base}reportes/cartera/?desde=${start}&hasta=${end}`)
        .then(setPortfolio)
        .catch((e) => setErr(e.message));
  }, [kind, base, start, end]);
  const opts = (
    key: string,
    label: (r: Row) => string,
    filter?: (r: Row) => boolean,
  ) =>
    (data[key] || [])
      .filter(filter || (() => true))
      .map((r) => ({ value: r.id, label: label(r) }));
  const accountOptions = opts(
    "cuentas",
    (r) => `${r.codigo} · ${r.nombre}`,
    (r) => r.movimiento && r.activa,
  );
  const bankOptions = opts("tesoreria", (r) => r.nombre);
  const thirdOptions = opts("terceros", (r) => r.nombre);
  const centerOptions = opts(
    "centros-costo",
    (r) => `${r.codigo} · ${r.nombre}`,
    (r) => r.activo,
  );
  const projectOptions = opts(
    "proyectos",
    (r) => `${r.codigo} · ${r.nombre}`,
    (r) => r.activo,
  );
  const docOptions = opts(
    "comprobantes",
    (r) => `${r.operacion} · ${r.secuencial} · ${r.tercero_nombre}`,
    (r) => r.estado === "contabilizado" && r.tipo !== "04",
  );
  const field = (f: Field) => (
    <FieldInput
      key={f.name}
      f={f}
      value={values[f.name] ?? f.default ?? ""}
      onChange={(v) => {
        setValues((x) => ({ ...x, [f.name]: v }));
        setPreview(null);
      }}
    />
  );
  const lineField = (i: number, f: Field) => (
    <FieldInput
      key={f.name}
      f={f}
      value={lines[i][f.name]}
      onChange={(v) =>
        setLines((old) =>
          old.map((r, n) => (n === i ? { ...r, [f.name]: v } : r)),
        )
      }
    />
  );
  const rateAmount = (b: string, r: string) =>
    decimal((cent(b) * cent(r) + 5000n) / 10000n);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      if (kind === "asiento")
        await post(`${base}asientos/`, {
          fecha: values.fecha,
          tipo: values.tipo_asiento || "diario",
          concepto: values.concepto,
          lineas: lines.map((l) => ({
            ...l,
            tercero: l.tercero || undefined,
            centro_costo: l.centro_costo || undefined,
            proyecto: l.proyecto || undefined,
          })),
        });
      if (kind === "documento") {
        const vat = ["no_objeto", "exento"].includes(values.tasa)
          ? "0"
          : values.tasa;
        const code =
          values.tasa === "no_objeto"
            ? "6"
            : values.tasa === "exento"
              ? "7"
              : values.tasa === "0"
                ? "0"
                : "4";
        await post(`${base}comprobantes/`, {
          ...values,
          soporte: values.soporte || undefined,
          distribucion: allocations.length ? allocations : undefined,
          ats_evidencia: values.no_retencion_motivo
            ? { no_retencion_motivo: values.no_retencion_motivo }
            : {},
          impuestos: [
            {
              codigo: "2",
              codigo_porcentaje: code,
              tasa: vat,
              base: values.base,
              valor: rateAmount(values.base, vat),
            },
          ],
        });
      }
      if (kind === "retencion")
        await post(`${base}retenciones/`, { ...values, detalles: lines });
      if (kind === "pago")
        await post(`${base}tesoreria/pagar/`, {
          ...values,
          tercero: values.tercero || undefined,
          contrapartida: values.contrapartida || undefined,
          distribucion: lines.length ? undefined : allocations,
          aplicaciones: lines.map((l) => ({
            partida: l.partida,
            importe: l.importe,
          })),
        });
      if (kind === "conciliacion")
        await post(`${base}conciliaciones/`, {
          ...values,
          asignaciones: lines,
        });
      if (kind === "extracto") {
        if (!files.length) throw new Error("Selecciona un archivo.");
        const f = new FormData();
        f.append("archivo", files[0]);
        const o: Row = {
          ...values,
          mapeo: mapping,
          confirmar_duplicados: values.confirmar_duplicados === "si",
        };
        if (!o.saldo_final) delete o.saldo_final;
        f.append("opciones", JSON.stringify(o));
        f.append("preview", preview ? "false" : "true");
        const r = await api(`${base}extractos/importar/`, {
          method: "POST",
          body: f,
        });
        if (!preview) {
          setPreview(r.filas);
          return;
        }
      }
      if (kind === "xml" || kind.startsWith("maestros-")) {
        if (!files.length)
          throw new Error("Selecciona archivos para importar.");
        const f = new FormData();
        files.forEach((file) =>
          f.append(kind === "xml" ? "archivos" : "archivo", file),
        );
        f.append(
          "opciones",
          JSON.stringify({
            fecha_registro: values.fecha_registro,
            cuenta: values.cuenta || undefined,
            centro_costo: values.centro_costo || undefined,
            proyecto: values.proyecto || undefined,
            confirmar_retencion: values.confirmar_retencion === "si",
          }),
        );
        const endpoint = kind === "xml" ? "comprobantes" : kind.split("-")[1];
        const r = await api(`${base}${endpoint}/importar/`, {
          method: "POST",
          body: f,
        });
        if (r.resultados) {
          setResult(r.resultados);
          if (r.resultados.some((x: Row) => x.error)) return;
        }
      }
      onComplete();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  return (
    <form onSubmit={submit}>
      {["xml", "extracto", "maestros-cuentas", "maestros-terceros"].includes(
        kind,
      ) && (
        <label className="upload-box">
          <Upload size={24} />
          <strong>
            {files.length
              ? files.map((f) => f.name).join(", ")
              : kind === "xml"
                ? "Selecciona archivos XML"
                : "Selecciona un archivo CSV o XLSX"}
          </strong>
          <small>Revisa los datos antes de confirmar la importación.</small>
          <input
            type="file"
            accept={kind === "xml" ? ".xml" : ".csv,.xlsx"}
            multiple={kind === "xml"}
            required
            onChange={(e) => {
              setFiles(Array.from(e.target.files || []));
              setPreview(null);
              setResult(null);
            }}
          />
        </label>
      )}
      <div className="form-grid">
        {kind === "documento" && (
          <>
            {field({
              name: "operacion",
              label: "Operación",
              options: [
                { value: "venta", label: "Venta" },
                { value: "compra", label: "Compra / gasto" },
              ],
            })}
            {field({
              name: "tipo",
              label: "Tipo de comprobante",
              options: [
                { value: "01", label: "Factura" },
                { value: "02", label: "Nota de venta" },
                { value: "03", label: "Liquidación de compra" },
                { value: "04", label: "Nota de crédito" },
                { value: "05", label: "Nota de débito" },
              ],
            })}
            {field({
              name: "tercero",
              label: "Cliente / proveedor",
              options: thirdOptions,
              span: true,
            })}
            {field({
              name: "cuenta",
              label: "Cuenta de ingreso o gasto",
              options: accountOptions,
              searchable: true,
              span: true,
            })}
            {field({ name: "establecimiento", label: "Establecimiento" })}
            {field({ name: "punto_emision", label: "Punto de emisión" })}
            {field({ name: "secuencial", label: "Secuencial" })}
            {field({ name: "autorizacion", label: "Autorización" })}
            {field({
              name: "fecha_emision",
              label: "Fecha de emisión",
              type: "date",
            })}
            {field({
              name: "fecha_registro",
              label: "Fecha de registro",
              type: "date",
            })}
            {field({
              name: "fecha_vencimiento",
              label: "Vencimiento",
              type: "date",
            })}
            {field({
              name: "base",
              label: "Base sin impuestos",
              placeholder: "100.00",
            })}
            {field({
              name: "tasa",
              label: "IVA",
              options: [
                { value: "15", label: "15 %" },
                { value: "0", label: "0 %" },
                { value: "no_objeto", label: "No objeto de IVA" },
                { value: "exento", label: "Exento" },
              ],
            })}
            {field({
              name: "sustento",
              label: "Sustento tributario",
              options: [
                { value: "01", label: "01 · Crédito IVA y costo/gasto IR" },
                { value: "02", label: "02 · Solo costo/gasto IR" },
              ],
            })}
            {field({
              name: "concepto",
              label: "Concepto / descripción del servicio",
              span: true,
            })}
            {["04", "05"].includes(values.tipo) &&
              field({
                name: "soporte",
                label: "Comprobante de soporte",
                options: docOptions,
                span: true,
              })}
          </>
        )}
        {kind === "documento" &&
          values.operacion === "compra" &&
          field({
            name: "no_retencion_motivo",
            label: "Justificación de no retención IR (si aplica el código 332)",
            required: false,
            span: true,
          })}
        {kind === "asiento" && (
          <>
            {field({ name: "fecha", label: "Fecha", type: "date" })}
            {field({
              name: "tipo_asiento",
              label: "Tipo de asiento",
              options: ["diario", "apertura", "ajuste", "cierre"].map((t) => ({
                value: t,
                label: t,
              })),
              default: "diario",
            })}
            {field({ name: "concepto", label: "Concepto", span: true })}
          </>
        )}
        {kind === "pago" && (
          <>
            {field({
              name: "tesoreria",
              label: "Cuenta de caja o banco",
              options: bankOptions,
              span: true,
            })}
            {field({
              name: "direccion",
              label: "Movimiento",
              options: [
                { value: "ingreso", label: "Ingreso / cobro" },
                { value: "egreso", label: "Egreso / pago" },
              ],
            })}
            {field({ name: "fecha", label: "Fecha", type: "date" })}
            {field({ name: "importe", label: "Importe total" })}
            {field({ name: "referencia", label: "Referencia bancaria" })}
            {field({
              name: "tercero",
              label: "Tercero",
              options: thirdOptions,
              required: false,
            })}
            {field({
              name: "contrapartida",
              label: "Contrapartida para comisión u otro movimiento",
              options: accountOptions,
              searchable: true,
              required: false,
            })}
            {field({ name: "concepto", label: "Concepto", span: true })}
          </>
        )}
        {kind === "retencion" && (
          <>
            {field({
              name: "soporte",
              label: "Comprobante de soporte",
              options: docOptions,
              span: true,
            })}
            {field({ name: "numero", label: "Número 001-001-000000001" })}
            {field({ name: "fecha", label: "Fecha de emisión", type: "date" })}
            {field({ name: "autorizacion", label: "Autorización", span: true })}
          </>
        )}
        {kind === "xml" && (
          <>
            {field({
              name: "fecha_registro",
              label: "Fecha de registro contable",
              type: "date",
            })}
            {field({
              name: "cuenta",
              label: "Clasificación contable",
              options: accountOptions,
              searchable: true,
              required: false,
            })}
            {field({
              name: "confirmar_retencion",
              label: "Si contiene retenciones",
              options: [
                { value: "no", label: "Detener para revisión" },
                {
                  value: "si",
                  label: "Confirmar retención y aplicar a cartera",
                },
              ],
              required: false,
            })}
            {field({
              name: "centro_costo",
              label: "Centro de costo para el lote",
              options: centerOptions,
              required: false,
            })}
            {field({
              name: "proyecto",
              label: "Proyecto para el lote",
              options: projectOptions,
              required: false,
            })}
          </>
        )}
        {kind === "extracto" && (
          <>
            {field({
              name: "tesoreria",
              label: "Cuenta bancaria",
              options: bankOptions,
              span: true,
            })}
            {field({
              name: "saldo_inicial",
              label: "Saldo inicial del extracto",
            })}
            {field({
              name: "saldo_final",
              label: "Saldo final",
              required: false,
            })}
            <div className="span2 form-section-label">
              Nombres de las columnas del archivo
            </div>
            {Object.keys(mapping).map((k) => (
              <FieldInput
                key={k}
                f={{
                  name: k,
                  label: k,
                  required: !["saldo", "referencia", "descripcion"].includes(k),
                }}
                value={mapping[k]}
                onChange={(v) => {
                  setMapping({ ...mapping, [k]: v });
                  setPreview(null);
                }}
              />
            ))}
            {field({
              name: "confirmar_duplicados",
              label: "Posibles duplicados de extractos anteriores",
              options: [
                { value: "no", label: "Detener para revisar" },
                { value: "si", label: "He revisado y confirmo su importación" },
              ],
              required: false,
            })}
          </>
        )}
        {kind === "conciliacion" && (
          <>
            {field({
              name: "tesoreria",
              label: "Cuenta bancaria",
              options: bankOptions,
            })}
            {field({
              name: "fecha",
              label: "Fecha de conciliación",
              type: "date",
            })}
            {field({
              name: "nota",
              label: "Nota",
              required: false,
              span: true,
            })}
          </>
        )}
      </div>
      {(kind === "documento" || (kind === "pago" && !lines.length)) && (
        <>
          <DistributionEditor
            value={allocations}
            onChange={setAllocations}
            total={kind === "documento" ? values.base : values.importe}
            data={data}
          />
          {kind === "documento" &&
            ["04", "05"].includes(values.tipo) &&
            !allocations.length && (
              <p className="muted">
                La nota heredará la distribución del comprobante de soporte.
                Puedes indicar otra distribución.
              </p>
            )}
        </>
      )}
      {["asiento", "pago", "retencion", "conciliacion"].includes(kind) && (
        <div className="repeat-lines">
          <div className="line-heading">
            <h3>
              {kind === "asiento"
                ? "Detalle del asiento"
                : kind === "pago"
                  ? "Aplicar a facturas (opcional)"
                  : kind === "retencion"
                    ? "Impuestos retenidos"
                    : "Asignaciones de conciliación"}
            </h3>
            <Button
              secondary
              onClick={() =>
                setLines((l) => [
                  ...l,
                  kind === "asiento"
                    ? { cuenta: "", debe: "0.00", haber: "0.00" }
                    : kind === "pago"
                      ? { partida: "", importe: "0.00" }
                      : kind === "retencion"
                        ? {
                            impuesto: "1",
                            codigo: "303",
                            base: "100.00",
                            porcentaje: "10",
                            valor: "10.00",
                          }
                        : { linea: "", movimiento: "", importe: "0.00" },
                ])
              }
            >
              <Plus size={14} />
              Agregar línea
            </Button>
          </div>
          {lines.map((_, i) => (
            <div className="repeat-row" key={i}>
              <div className="form-grid">
                {kind === "asiento" && (
                  <>
                    {lineField(i, {
                      name: "cuenta",
                      label: "Cuenta",
                      options: accountOptions,
                      searchable: true,
                      span: true,
                    })}
                    {lineField(i, {
                      name: "tercero",
                      label: "Tercero",
                      options: thirdOptions,
                      required: false,
                    })}
                    {lineField(i, {
                      name: "descripcion",
                      label: "Descripción",
                      required: false,
                    })}
                    {lineField(i, {
                      name: "centro_costo",
                      label: "Centro de costo",
                      options: centerOptions,
                      required: false,
                    })}
                    {lineField(i, {
                      name: "proyecto",
                      label: "Proyecto",
                      options: projectOptions,
                      required: false,
                    })}
                    {lineField(i, { name: "debe", label: "Debe" })}
                    {lineField(i, { name: "haber", label: "Haber" })}
                  </>
                )}
                {kind === "pago" && (
                  <>
                    {lineField(i, {
                      name: "partida",
                      label: "Partida pendiente",
                      options: portfolio
                        .filter(
                          (p) =>
                            p.tipo ===
                            (values.direccion === "ingreso"
                              ? "cobrar"
                              : "pagar"),
                        )
                        .map((p) => ({
                          value: p.id,
                          label: `#${p.id} · ${p.tercero} · ${fmt(p.pendiente)}`,
                        })),
                      span: true,
                    })}
                    {lineField(i, {
                      name: "importe",
                      label: "Importe a aplicar",
                    })}
                  </>
                )}
                {kind === "retencion" && (
                  <>
                    {lineField(i, {
                      name: "impuesto",
                      label: "Impuesto",
                      options: [
                        { value: "1", label: "Renta" },
                        { value: "2", label: "IVA" },
                      ],
                    })}
                    {lineField(i, {
                      name: "codigo",
                      label: "Código de retención",
                    })}
                    {lineField(i, { name: "base", label: "Base imponible" })}
                    {lineField(i, { name: "porcentaje", label: "Porcentaje" })}
                    {lineField(i, { name: "valor", label: "Valor retenido" })}
                    <button
                      type="button"
                      className="text-button"
                      onClick={() =>
                        setLines((l) =>
                          l.map((r, n) =>
                            n === i
                              ? {
                                  ...r,
                                  valor: rateAmount(r.base, r.porcentaje),
                                }
                              : r,
                          ),
                        )
                      }
                    >
                      Calcular valor
                    </button>
                  </>
                )}
                {kind === "conciliacion" && (
                  <>
                    {lineField(i, {
                      name: "linea",
                      label: "Línea del extracto",
                      options: opts(
                        "extracto-lineas",
                        (r) =>
                          `#${r.id} · ${r.fecha} · ${r.referencia} · ${fmt(r.importe)}`,
                      ),
                      span: true,
                    })}
                    {lineField(i, {
                      name: "movimiento",
                      label: "Movimiento contable",
                      options: opts(
                        "movimientos",
                        (r) =>
                          `#${r.id} · ${r.fecha} · ${r.referencia} · ${fmt(r.importe)}`,
                      ),
                      span: true,
                    })}
                    {lineField(i, {
                      name: "importe",
                      label: "Importe a conciliar",
                    })}
                  </>
                )}
              </div>
              <button
                type="button"
                className="remove-line"
                onClick={() => setLines((l) => l.filter((_, n) => n !== i))}
                aria-label="Eliminar línea"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      {kind === "asiento" && (
        <div className="entry-totals">
          <span>
            Debe{" "}
            <b>
              {fmt(
                decimal(lines.reduce((s, l) => s + cent(l.debe || "0"), 0n)),
              )}
            </b>
          </span>
          <span>
            Haber{" "}
            <b>
              {fmt(
                decimal(lines.reduce((s, l) => s + cent(l.haber || "0"), 0n)),
              )}
            </b>
          </span>
          <Badge
            tone={
              lines.reduce(
                (s, l) => s + cent(l.debe || "0") - cent(l.haber || "0"),
                0n,
              ) === 0n
                ? "green"
                : "amber"
            }
          >
            {lines.reduce(
              (s, l) => s + cent(l.debe || "0") - cent(l.haber || "0"),
              0n,
            ) === 0n
              ? "Cuadrado"
              : "Revisa la diferencia"}
          </Badge>
        </div>
      )}
      {kind === "documento" && (
        <div className="entry-totals">
          <span>
            Base <b>{fmt(values.base)}</b>
          </span>
          <span>
            IVA{" "}
            <b>
              {fmt(
                rateAmount(
                  values.base,
                  ["no_objeto", "exento"].includes(values.tasa)
                    ? "0"
                    : values.tasa,
                ),
              )}
            </b>
          </span>
          <span>
            Total{" "}
            <b>
              {fmt(
                decimal(
                  cent(values.base) +
                    cent(
                      rateAmount(
                        values.base,
                        ["no_objeto", "exento"].includes(values.tasa)
                          ? "0"
                          : values.tasa,
                      ),
                    ),
                ),
              )}
            </b>
          </span>
        </div>
      )}
      {kind === "pago" && (
        <div className="notice">
          <CircleHelp size={18} />
          <p>
            El importe no aplicado a facturas se registra como anticipo del
            tercero, o contra la contrapartida seleccionada.
          </p>
        </div>
      )}
      {kind.startsWith("maestros-") && (
        <div className="notice">
          <FileText size={18} />
          <p>
            {kind.endsWith("cuentas")
              ? "Columnas: codigo, nombre, grupo, naturaleza, padre, movimiento. Padres antes de sus hijas."
              : "Columnas: tipo_identificacion, identificacion, nombre, cliente, proveedor. Identificaciones como texto."}
          </p>
        </div>
      )}
      {preview && (
        <>
          <h3>Vista previa · {preview.length} filas</h3>
          <DynamicTable rows={preview} />
        </>
      )}
      {result && <DynamicTable rows={result} />}
      {err && (
        <div className="alert error" role="alert">
          {err}
        </div>
      )}
      <div className="form-footer">
        <span>
          {kind === "documento" || kind === "asiento"
            ? "Se guardará como borrador para revisión."
            : "La operación se registra en esta empresa."}
        </span>
        <Button type="submit" disabled={loading}>
          {loading ? (
            <Loader2 size={16} className="spin" />
          ) : (
            <Check size={16} />
          )}{" "}
          {kind === "extracto"
            ? preview
              ? "Confirmar importación"
              : "Ver vista previa"
            : ["xml", "maestros-cuentas", "maestros-terceros"].includes(kind)
              ? "Importar archivos"
              : "Guardar"}
        </Button>
      </div>
    </form>
  );
}
